// JSON parse of Map objects

import { NamedIdentityDTO } from '../api/api';
import { ColumnType, IDAndTypeInterface, ListColumn } from './global-interfaces';

/**
 * Transforms values of a Map into an object for storage as json
 * @param key
 * @param value
 */
export function getJSONFromMap(key: any, value: any) {
  if (value instanceof Map) {
    return {
      dataType: 'Map',
      value: Array.from(value.entries()), // or with spread: value: [...value]
    };
  } else {
    return value;
  }
}

/**
 * Transforms value of an JSON object to a map
 * @param key
 * @param value
 */
export function getMapFromJSON(key: any, value: any) {
  if (typeof value === 'object' && value !== null) {
    if (value.dataType === 'Map') {
      return new Map(value.value);
    }
  }
  return value;
}

/**
 * Returns the provided NamedIdentity array as string, e.g. for Query params
 * @param item
 */
export function getItemId<T extends NamedIdentityDTO>(item: T): string {
  const arr = [];
  if (item) {
    arr.push(item.id);
  }
  return JSON.stringify(arr);
}

/**
 * checks if value is in Enum
 * @param enumObj add the enum as reference
 * @param value value to be checked
 */
export function isEnumValue<T extends Record<string, string | number>>(enumObj: T, value: string | number): boolean {
  return Object.values(enumObj).includes(value as T[keyof T]);
}

/**
 * return the keys of an enum as Array of type of keys
 * @param obj
 */
export function enumKeys<O extends object, K extends keyof O = keyof O>(obj: O): K[] {
  return Object.keys(obj).filter(k => Number.isNaN(+k)) as K[];
}

/**
 * Checks if the given number is even.
 * @param num
 */
export function isNumberEven(num: number) {
  return num % 2 === 0;
}

/**
 * Returns all Properties filtered on private fields and the field attribute
 * @param prefix (e.g. 'location.address.', 'company.', 'location.', ...)
 * @param dtoProps (call with Object.keys(new DTO()), e.g. Object.keys(new ScreenDTO())
 */
export function getPropsFromDTO(prefix: string, dtoProps: string[]): string[] {
  return dtoProps
    .filter(prop => !prop.startsWith('_')) // remove private fields
    .filter(prop => prop !== 'attributes') // filter out attributes since they are handled separately
    .map(prop => (prefix ? `${prefix}.${prop}` : prop)); // set prefix if provided
}

/**
 * This function creates "ListColumns" for variable column pages like devices, locations, ...
 * @param attributes list of attributes to transform to ListColumns
 * @param attributeType the Attribute Type of that column, e.g. 'DEVICE_ATTRIBUTE'
 */
export function createAttributeColumns(attributes: NamedIdentityDTO[], attributeType: ColumnType): ListColumn[] {
  return (attributes || []).map(attribute => {
    return {
      id: attribute.id,
      title: attribute.name ?? attribute.id ?? attribute.toString(),
      selected: false,
      type: attributeType,
    };
  });
}

/**
 * This function checks whether two "Columns" match, for variable column pages like devices, locations, ...
 * @param colA first column
 * @param colB second column
 */
export function isColumnMatch(colA: IDAndTypeInterface, colB: IDAndTypeInterface): boolean {
  return colA.id === colB.id && colA.type === colB.type;
}
